import { CheckCircle, Grid, Loader } from 'phosphor-icons';
import { interpolateStringWithData } from 'utils';
import { c } from '../../../utils/tailwind-merge';

import { BadgePropsData } from '../../atoms/Badge/Badge.types';
import { Button } from '../../atoms/Button/Button';
import { Price } from '../../atoms/Price/Price';
import { BadgeList } from '../../molecules/BadgeList/BadgeList';
import { PublisherInfo } from '../../molecules/PublisherInfo/PublisherInfo';
import { RichText } from '../../molecules/RichText/RichText';
import {
  ProductInfoProps,
  ProductInfoPropsActions,
  ProductInfoPropsMicrocopy,
} from './ProductInfo.types';

const flexItemBadge = 'flex items-center';

const VerifiedSolutionsTooltipContent = ({
  title,
  benefit1,
  benefit2,
  benefit3,
  cta,
}: ProductInfoPropsMicrocopy['verifiedSolutionsTooltip']) => (
  <>
    {title && (
      <RichText
        html={title}
        className="text-sm [&_a]:text-sm [&_a]:no-underline [&_span]:mb-0 [&_span]:text-grey-700"
      />
    )}
    {benefit1 && (
      <div className="px-3 py-2 pb-0">
        <div className={flexItemBadge}>
          <CheckCircle
            className="mr-3 mt-1 inline-block text-blue"
            size={25}
            aria-hidden="true"
          />
          <RichText
            html={benefit1}
            className="py-1.5 text-sm [&_a]:no-underline"
            type="p"
          />
        </div>
        {benefit2 && (
          <div className={flexItemBadge}>
            <div className="mr-1 pt-1">
              <Loader />
            </div>
            <RichText html={benefit2} className="py-1.5 text-sm" />
          </div>
        )}
        {benefit3 && (
          <div className={flexItemBadge}>
            <div className="mr-1 pt-1">
              <Grid />
            </div>
            <RichText html={benefit3} className="py-1.5 text-sm" />
          </div>
        )}
      </div>
    )}
    {cta && (
      <div className="mt-1 py-1">
        <RichText
          html={cta}
          className="py-1.5 text-sm [&_a]:no-underline [&_p]:mb-0"
          type="p"
        />
      </div>
    )}
  </>
);

export const renderBadges = ({ isUpm, isVerifiedSolutions, microcopy }) => {
  const badges: BadgePropsData[] = [];

  if (isUpm) {
    badges.push({
      text: microcopy.upm,
      popover: (
        <RichText
          html={microcopy.upmTooltip}
          className="text-sm [&_a]:text-sm [&_a]:no-underline [&_p]:mb-0 [&_span]:mb-0 [&_span]:text-grey-700"
        />
      ),
      interactive: true,
    });
  }

  if (isVerifiedSolutions) {
    badges.push({
      text: microcopy.verifiedSolutions,
      popover: VerifiedSolutionsTooltipContent(
        microcopy.verifiedSolutionsTooltip
      ),
      interactive: true,
    });
  }

  return <BadgeList badges={badges} />;
};

const renderCtaButton = ({
  type,
  nbOfPackages,
  isEntitled,
  microcopy,
  onClickProductInfo,
}: Pick<
  ProductInfoProps,
  'type' | 'nbOfPackages' | 'isEntitled' | 'microcopy'
> &
  ProductInfoPropsActions & { className?: string }) => {
  const label =
    type === 'single-package'
      ? isEntitled
        ? microcopy.openInUnity
        : microcopy.addToMyAssetSinglePackage
      : isEntitled
        ? microcopy.seeOwnedPackages
        : interpolateStringWithData(microcopy.addToMyAssetMultiPackages, {
            number: nbOfPackages,
          });

  return (
    <Button
      label={label}
      style={!isEntitled ? 'primary' : 'secondary'}
      size="medium"
      edge="square"
      onClick={onClickProductInfo}
      className="w-fit lg:w-full"
    />
  );
};

const renderEulaMessage = ({
  customEula,
  microcopy,
}: Pick<ProductInfoProps, 'customEula' | 'microcopy'>) => {
  const isStandardEula = !customEula;
  const sharedRichTextProps = {
    className:
      'text-xs text-grey-700 [&_a]:text-xs [&_a]:no-underline [&_p]:mb-0',
  };
  return isStandardEula ? (
    <RichText html={microcopy.standardEulaMessage} {...sharedRichTextProps} />
  ) : (
    <RichText
      html={interpolateStringWithData(microcopy.customEulaMessage, {
        custom: customEula ?? '',
      })}
      {...sharedRichTextProps}
    />
  );
};

export const ProductInfo = ({
  variant = 'default',
  isUpm,
  isVerifiedSolutions,
  productTitle,
  publisherName,
  publisherLink,
  isCertifiedPublisher,
  price,
  type,
  isEntitled,
  nbOfPackages,
  microcopy,
  className,
  onClickProductInfo,
  onCallToActionMount,
  customEula,
}: ProductInfoProps & ProductInfoPropsActions) => {
  return variant === 'default' ? (
    <div
      data-test={'product-info-section'}
      className={c('flex flex-col gap-y-4', className || '')}
    >
      {renderBadges({ isUpm, isVerifiedSolutions, microcopy })}

      <h3
        data-test="product-title"
        className="text-gray-900 text-xl font-semibold leading-8"
      >
        {productTitle}
      </h3>

      <PublisherInfo
        showAvatar={true}
        isCertified={isCertifiedPublisher}
        publisherName={publisherName}
        publisherLink={publisherLink}
        microcopy={{ certifiedPublisher: microcopy.certifiedPublisher }}
      />

      <Price {...price} microcopy={microcopy.price} />

      <div ref={onCallToActionMount}>
        {renderCtaButton({
          type,
          isEntitled,
          nbOfPackages,
          microcopy,
          onClickProductInfo,
        })}
      </div>
      {!isEntitled && renderEulaMessage({ customEula, microcopy })}
    </div>
  ) : (
    <div
      className={c(
        'fixed bottom-0 left-0 right-0 z-50 border border-grey-300 bg-grey-100 lg:fixed lg:bottom-auto lg:top-0',
        className || ''
      )}
    >
      <div className="lg-container flex w-full items-start justify-between py-3">
        <div className="hidden flex-col lg:flex">
          <h3 className="text-gray-900 text-md font-medium leading-7">
            {productTitle}
          </h3>
          <div className="flex items-center">
            <PublisherInfo
              size="small"
              showAvatar={false}
              isCertified={isCertifiedPublisher}
              publisherName={publisherName}
              publisherLink={publisherLink}
              microcopy={{ certifiedPublisher: microcopy.certifiedPublisher }}
            />
          </div>
        </div>

        <div className="flex w-full flex-col gap-2 lg:max-w-[496px]">
          <div className="flex w-full items-center justify-between gap-4 lg:gap-8">
            <Price {...price} microcopy={microcopy.price} />

            <div ref={onCallToActionMount} className="lg:w-full">
              {renderCtaButton({
                type,
                isEntitled,
                nbOfPackages,
                microcopy,
                onClickProductInfo,
              })}
            </div>
          </div>
          {!isEntitled && renderEulaMessage({ customEula, microcopy })}
        </div>
      </div>
    </div>
  );
};
